<div class="container-fluid">
  <div class="row">
    <div class="col-12 px-0">
      <div class="card p-0 border-0">
        <div class="card-body p-3">
          <!-- Header Section -->
          <div class="d-flex flex-row flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h1 class="card-title mb-1">Employee Directory</h1>
              <span class="sub-text">Manage your workforce, roles, and administrative access</span>
            </div>

            <div class="d-flex gap-2">
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 px-3 py-2"
                (click)="onExportEmployees()"
                [disabled]="isExporting">
                @if (isExporting) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Export Employees
                }
              </button>
              <button class="btn btn-primary d-flex align-items-center px-4 py-2" (click)="onAddNewEmployee()">
                <i class="bi bi-person-plus"></i>
                <span>Add New Employee</span>
              </button>
            </div>
          </div>

          <!-- Modern Filter Bar -->
          <div class="row g-3 mb-4">
            <div class="col-md-8 ps-0">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 ps-3 rounded-0">
                  <i class="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  class="form-control ps-2 rounded-0 border-start-0"
                  placeholder="Search by team member name or employee ID..."
                  [(ngModel)]="searchTerm"
                  (keyup)="onSearchKeyUp()" />
              </div>
            </div>

            <div class="col-md-4 pe-0">
              <ng-select
                placeholder="All Designations"
                [items]="designations"
                [(ngModel)]="selectedDesignationId"
                (ngModelChange)="onDesignationFilterChange()"
                bindValue="id"
                bindLabel="title"
                [clearable]="true"
                [searchable]="true" />
            </div>
          </div>

          @if (isEmployeesDataLoading) {
            <app-loading-spinner></app-loading-spinner>
          } @else {
            @if ((employees || []).length === 0) {
              <div class="empty-state-container">
                <i class="bi bi-people"></i>
                <h5 class="text-dark fw-bold">No Employees Registered</h5>
                <p class="text-muted mb-4">Start by adding your first team member to the system.</p>
              </div>
            } @else {
              <div class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th class="ps-4 border-0 text-uppercase small fw-bold text-muted py-3">Team Member</th>
                      <th class="border-0 text-uppercase small fw-bold text-muted py-3">
                        <button
                          type="button"
                          class="btn btn-link p-0 border-0 text-uppercase small fw-bold text-muted text-decoration-none d-inline-flex align-items-center gap-1"
                          (click)="onToggleEmployeeIdSort()"
                          [title]="sortDirection === 'asc' ? 'Sorted low to high' : 'Sorted high to low'">
                          Employee ID
                          <i
                            class="bi"
                            [ngClass]="sortDirection === 'asc' ? 'bi-sort-numeric-down' : 'bi-sort-numeric-up-alt'"></i>
                        </button>
                      </th>
                      <th class="border-0 text-uppercase small fw-bold text-muted py-3">Designation</th>
                      <th class="border-0 text-uppercase small fw-bold text-muted py-3">Joined On</th>
                      <th class="pe-4 border-0 text-uppercase small fw-bold text-muted py-3 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (emp of employees; track emp.id) {
                      <tr class="cursor-pointer transition-all border-bottom" [routerLink]="['/employees', emp.id]">
                        <td class="ps-4 py-3">
                          <div class="d-flex align-items-center">
                            <div class="position-relative">
                              <img
                                [src]="
                                  emp.user.profile_picture ||
                                  getDefaultProfilePicUrl(emp.user.first_name, emp.user.last_name)
                                "
                                alt="{{ emp.user.first_name }} {{ emp.user.last_name }}"
                                class="rounded-circle border border-2 border-white shadow-sm bg-light"
                                width="42"
                                height="42"
                                (error)="handleImageError($event)" />
                              <span
                                class="position-absolute bottom-0 end-0 bg-success border border-white border-2 rounded-circle p-1"></span>
                            </div>
                            <div class="ms-3">
                              <div class="fw-bold text-dark">
                                {{ emp.user.first_name + ' ' + emp.user.last_name }}
                              </div>
                              <div class="small text-muted">{{ emp.user.email }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="py-3">
                          <span class="app-badge badge-secondary">
                            {{ emp.employee_number }}
                          </span>
                        </td>
                        <td class="py-3">
                          <div class="d-flex flex-column">
                            <span class="fw-semibold text-dark">
                              {{ emp.designation.title }}
                            </span>
                            <span class="small text-muted">Active Member</span>
                          </div>
                        </td>
                        <td class="py-3">
                          <span class="text-muted small">
                            <i class="bi bi-calendar3 me-1"></i>
                            {{ emp.date_of_joining | date }}
                          </span>
                        </td>
                        <td class="pe-4 py-3 text-end" (click)="$event.stopPropagation()">
                          <div class="d-flex gap-2 justify-content-end">
                            <button class="btn btn-action-edit" (click)="editEmployee(emp)" title="Edit employee">
                              <i class="bi bi-pencil-square"></i>
                            </button>
                            <button
                              class="btn btn-action-delete"
                              (click)="onClickDeleteEmployee(emp.id)"
                              title="Delete employee">
                              <i class="bi bi-trash3"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <div class="mt-4">
                <app-pagination
                  [currentPage]="currentPage"
                  [totalCount]="totalCount"
                  [pageSize]="10"
                  [hasNextPage]="hasNextPage"
                  [hasPreviousPage]="hasPreviousPage"
                  (pageChange)="onPageChange($event)"></app-pagination>
              </div>
            }
          }
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #addNewEmployeeContent let-dismiss="dismiss">
  <div class="modal-content border-0 overflow-hidden">
    <div class="modal-header">
      <div>
        <h5 class="modal-title mb-0">
          {{ employeeToBeEdit ? 'Update Employee Profile' : 'Onboard New Employee' }}
        </h5>
        <div class="sub-text small">Assign roles and personal details</div>
      </div>
      <button type="button" class="btn-close" (click)="dismiss()"></button>
    </div>

    <div class="modal-body p-0">
      <app-employee-info-form
        [employee]="employeeToBeEdit"
        [mode]="employeeToBeEdit ? Action.EDIT : Action.CREATE"
        (onCancelEvent)="dismiss()"
        (onSaveEvent)="onSaveEmployeeInfo($event)" />
    </div>
  </div>
</ng-template>

<ng-template #deleteEmployeeContent let-dismiss="dismiss">
  <div class="modal-content border-0 rounded-4 overflow-hidden">
    <div class="modal-header">
      <h5 class="modal-title">
        <i class="bi bi-exclamation-triangle text-danger me-2"></i>
        Terminate Record
      </h5>
      <button type="button" class="btn-close" (click)="dismiss()"></button>
    </div>

    <div class="modal-body p-4 text-center">
      <div class="bg-danger bg-opacity-10 d-inline-block p-3 rounded-circle mb-3">
        <i class="bi bi-person-x text-danger fs-1"></i>
      </div>
      <h5 class="fw-bold text-dark">Confirm Deletion</h5>
      <p class="text-muted">
        Are you sure you want to remove this employee from the system? This record cannot be recovered.
      </p>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="dismiss()">
        <i class="bi bi-x-circle"></i>
        <span>Cancel</span>
      </button>
      <button type="button" class="btn btn-danger rounded-0" (click)="onDeleteEmployee()">
        <i class="bi bi-trash3"></i>
        <span>Delete</span>
      </button>
    </div>
  </div>
</ng-template>
